import React from "react";
import { Check } from "lucide-react";
import { Button, Modal } from "../../../components/ui";
import { formatPrice } from "../../brand/planFormat";
import { formatLongDate } from "../enquiryMapping";
import type { EnquiryPayment } from "../types/enquiries.types";

export interface PaymentRequestedModalProps {
  /** The payment as it was stored — its date and amount are the backend's. */
  payment: EnquiryPayment;
  /** Who it went to, salutation and all: "Mr. Subham Patil". */
  recipient: string;
  /** Their address, or empty where the enquiry has none on record. */
  email: string;
  /** Stays on the enquiry, where the request is now a row on the page. */
  onViewDetails: () => void;
  /** Leaves for the enquiry list, this one now sitting in it as sent. */
  onGoToEnquiries: () => void;
}

/**
 * What the request turned into, once it has gone.
 *
 * No heading bar: the tick and the line under it are the whole message, and a
 * "Payment request sent" title above a panel that says so again would be the
 * same sentence twice. The dialog is still named to a screen reader — see
 * `Modal`'s `hideTitle`.
 *
 * It has no dismiss of its own beyond its two actions, both of which lead
 * somewhere: closing it by pressing Escape or the backdrop lands on the
 * enquiry, which is the same as View details.
 */
const PaymentRequestedModal: React.FC<PaymentRequestedModalProps> = ({
  payment,
  recipient,
  email,
  onViewDetails,
  onGoToEnquiries,
}) => (
  <Modal
    open
    hideTitle
    title="Payment request sent"
    onClose={onViewDetails}
    className="max-w-[500px]"
  >
    <div className="pt-3 text-center">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#E7F8EF]">
        <Check aria-hidden="true" className="h-7 w-7 text-[#2F8E5B]" />
      </span>

      <h3 className="mt-5 text-[20px] 3xl:text-[22px] font-medium text-ink">
        Payment request sent!
      </h3>

      <p className="mx-auto mt-2 max-w-[380px] text-[14px] 3xl:text-[15px] leading-6 text-ink-muted">
        A secure payment request of{" "}
        <span className="font-medium text-ink">
          {formatPrice(payment.amount)}
        </span>{" "}
        has been successfully generated and sent to{" "}
        <span className="font-medium text-ink">{recipient}</span>
        {email ? "’s email." : "."}
      </p>
    </div>

    <dl className="mt-6 rounded-md border border-[#DCF0F4] bg-[#F4FBFC] px-4 py-3.5 text-[13px] 3xl:text-[14px]">
      <div className="flex items-center justify-between gap-4">
        <dt className="text-ink-muted">Plan</dt>
        <dd className="text-right text-ink">
          {payment.planName}
          <span aria-hidden="true" className="mx-1.5 text-ink-subtle">
            •
          </span>
          {payment.duration}
        </dd>
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-4">
        <dt className="text-ink-muted">Date</dt>
        <dd className="text-ink">{formatLongDate(payment.requestedAt)}</dd>
      </div>
      {/* The number the backend put on the invoice, which is what the client
          will quote back and what this request is findable by. Only shown when
          there is one — nothing here should read as a blank invoice. */}
      {payment.invoiceNumber && (
        <div className="mt-2.5 flex items-center justify-between gap-4">
          <dt className="text-ink-muted">Invoice</dt>
          <dd className="text-ink">{payment.invoiceNumber}</dd>
        </div>
      )}
    </dl>

    {/* The actions sit in the body rather than in `Modal`'s footer: they are
        two equal ways on from here, side by side and sharing the width, not
        the cancel-and-confirm pair the footer lays out. */}
    <div className="mt-5 flex flex-col-reverse gap-3 border-t border-line pt-5 sm:flex-row">
      <Button
        variant="secondary"
        size="md"
        fullWidth
        className="sm:flex-1"
        onClick={onViewDetails}
      >
        View details
      </Button>
      <Button
        size="md"
        fullWidth
        className="sm:flex-1"
        onClick={onGoToEnquiries}
      >
        Go to enquiries
      </Button>
    </div>
  </Modal>
);

export default PaymentRequestedModal;
